import React, { useEffect, useCallback, useMemo } from "react";
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from "common";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";

function AuthLoading(props) {
    const { t } = useTranslation();
    const {
        fetchUser,
        fetchCarTypes,
        fetchSettings,
        fetchBookings,
        fetchCancelReasons,
        fetchPromos,
        fetchDriverEarnings,
        fetchUsers,
        fetchNotifications,
        fetchEarningsReport,
        signOff,
        fetchWithdraws,
        fetchPaymentMethods,
        fetchLanguages,
        fetchWalletHistory,
        fetchCars,
        fetchCategories,
        fetchZoneData,
        fetchCountryData,
        fetchComplain,
        fetchAddMoney,
        fetchSMTP,
        fetchAddOn,
        fetchFeatureFlags,
    } = api;
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    const languagedata = useSelector((state) => state.languagedata);
    const settingsdata = useSelector((state) => state.settingsdata);
    const addonsdata = useSelector((state) => state.addondata);

    // Debug addon loading in AuthLoading
    useEffect(() => {
        console.log("🔍 AuthLoading - Addon State:", {
            full_state: addonsdata,
            data: addonsdata?.data,
            loading: addonsdata?.loading,
            error: addonsdata?.error,
        });
    }, [addonsdata]);

    // Memoize dispatch functions to prevent unnecessary re-renders
    const memoizedFetchSettings = useCallback(() => {
        dispatch(fetchSettings());
    }, [dispatch, fetchSettings]);

    const memoizedFetchLanguages = useCallback(() => {
        dispatch(fetchLanguages());
    }, [dispatch, fetchLanguages]);

    const memoizedFetchCarTypes = useCallback(() => {
        dispatch(fetchCarTypes());
    }, [dispatch, fetchCarTypes]);

    const memoizedFetchZoneData = useCallback(() => {
        dispatch(fetchZoneData());
    }, [dispatch, fetchZoneData]);

    const memoizedFetchCountryData = useCallback(() => {
        dispatch(fetchCountryData());
    }, [dispatch, fetchCountryData]);

    const memoizedFetchCategories = useCallback(() => {
        dispatch(fetchCategories());
    }, [dispatch, fetchCategories]);

    const memoizedFetchUser = useCallback(() => {
        dispatch(fetchUser());
    }, [dispatch, fetchUser]);

    // Memoize role-based fetch functions
    const fetchCustomerData = useCallback(() => {
        dispatch(fetchBookings());
        dispatch(fetchWalletHistory());
        dispatch(fetchCancelReasons());
        dispatch(fetchPaymentMethods());
        dispatch(fetchUsers());
    }, [
        dispatch,
        fetchBookings,
        fetchWalletHistory,
        fetchCancelReasons,
        fetchPaymentMethods,
        fetchUsers,
    ]);

    const fetchDriverData = useCallback(() => {
        dispatch(fetchBookings());
        dispatch(fetchWithdraws());
        dispatch(fetchCars());
        dispatch(fetchWalletHistory());
        dispatch(fetchPaymentMethods());
    }, [
        dispatch,
        fetchBookings,
        fetchWithdraws,
        fetchCars,
        fetchWalletHistory,
        fetchPaymentMethods,
    ]);

    const fetchAdminData = useCallback(() => {
        dispatch(fetchUsers());
        dispatch(fetchBookings());
        dispatch(fetchPromos());
        dispatch(fetchDriverEarnings());
        dispatch(fetchNotifications());
        dispatch(fetchEarningsReport());
        dispatch(fetchCancelReasons());
        dispatch(fetchWithdraws());
        dispatch(fetchPaymentMethods());
        dispatch(fetchCars());
        dispatch(fetchZoneData());
        dispatch(fetchCountryData());
        dispatch(fetchComplain());
        dispatch(fetchAddMoney());
        dispatch(fetchSMTP());
    }, [
        dispatch,
        fetchUsers,
        fetchBookings,
        fetchPromos,
        fetchDriverEarnings,
        fetchNotifications,
        fetchEarningsReport,
        fetchCancelReasons,
        fetchWithdraws,
        fetchPaymentMethods,
        fetchCars,
        fetchZoneData,
        fetchCountryData,
        fetchComplain,
        fetchAddMoney,
        fetchSMTP,
    ]);

    const fetchFleetAdminData = useCallback(() => {
        dispatch(fetchUsers());
        dispatch(fetchBookings());
        dispatch(fetchDriverEarnings());
        dispatch(fetchCars());
        dispatch(fetchCancelReasons());
        dispatch(fetchWithdraws());
        dispatch(fetchWalletHistory());
        dispatch(fetchPaymentMethods());
    }, [
        dispatch,
        fetchUsers,
        fetchBookings,
        fetchDriverEarnings,
        fetchCars,
        fetchCancelReasons,
        fetchWithdraws,
        fetchWalletHistory,
        fetchPaymentMethods,
    ]);

    // Memoize settings object to prevent unnecessary re-renders
    const settings = useMemo(
        () => settingsdata.settings,
        [settingsdata.settings]
    );
    const langlist = useMemo(
        () => languagedata.langlist,
        [languagedata.langlist]
    );
    const profile = useMemo(() => auth.profile, [auth.profile]);

    useEffect(() => {
        console.log("🚀 AuthLoading: Dispatching initial data fetches...");
        memoizedFetchSettings();
        console.log("🚀 AuthLoading: Dispatching fetchAddOn...");
        dispatch(fetchAddOn());
        console.log("🚀 AuthLoading: Dispatching fetchFeatureFlags...");
        dispatch(fetchFeatureFlags());
    }, [memoizedFetchSettings, dispatch, fetchAddOn, fetchFeatureFlags]);

    useEffect(() => {
        if (langlist) {
            try {
                for (const value of Object.values(langlist)) {
                    if (value.default === true) {
                        i18n.addResourceBundle(
                            value.langLocale,
                            "translations",
                            value.keyValuePairs
                        );
                        i18n.changeLanguage(value.langLocale);
                        moment.locale(value.dateLocale);
                        break; // Only set the first default language
                    }
                }
                memoizedFetchUser();
            } catch (error) {
                console.error("Error setting up language:", error);
            }
        }
    }, [langlist, memoizedFetchUser]);

    useEffect(() => {
        if (settings) {
            try {
                memoizedFetchLanguages();
                memoizedFetchCarTypes();
                memoizedFetchZoneData();
                memoizedFetchCountryData();
                memoizedFetchCategories();
                document.title = settings.appName;
            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        }
    }, [
        settings,
        memoizedFetchLanguages,
        memoizedFetchCarTypes,
        memoizedFetchZoneData,
        memoizedFetchCountryData,
        memoizedFetchCategories,
    ]);

    useEffect(() => {
        if (profile && profile.usertype) {
            try {
                const role = profile.usertype;

                switch (role) {
                    case "customer":
                        fetchCustomerData();
                        break;
                    case "driver":
                        fetchDriverData();
                        break;
                    case "admin":
                        fetchAdminData();
                        break;
                    case "fleetadmin":
                        fetchFleetAdminData();
                        break;
                    default:
                        console.error("Invalid user type:", role);
                        alert(t("not_valid_user_type"));
                        dispatch(signOff());
                        break;
                }
            } catch (error) {
                console.error("Error fetching role-based data:", error);
                alert(t("user_issue_contact_admin"));
                dispatch(signOff());
            }
        } else if (profile && !profile.usertype) {
            console.error("User profile missing usertype");
            alert(t("user_issue_contact_admin"));
            dispatch(signOff());
        }
    }, [
        profile,
        fetchCustomerData,
        fetchDriverData,
        fetchAdminData,
        fetchFleetAdminData,
        dispatch,
        signOff,
        t,
    ]);

    // Show loading states
    if (settingsdata.loading) {
        return <CircularLoading />;
    }

    if (!settings) {
        return (
            <div
                style={{
                    padding: "20px",
                    textAlign: "center",
                    fontFamily: "Arial, sans-serif",
                }}
            >
                <span>No Database Settings found</span>
            </div>
        );
    }

    if (auth.loading || !langlist) {
        return <CircularLoading />;
    }

    return props.children;
}

export default AuthLoading;
